import React from 'react';
import { Newspaper, Calendar, Image, BookOpen, LucideIcon } from 'lucide-react';

type SelectedType = 'articles' | 'events' | 'albums' | 'canticles' | 'books';

interface EntityCounts {
  drafted: number;
  published: number;
}

interface EntityStats {
  articles: EntityCounts;
  events: EntityCounts;
  albums: EntityCounts;
  canticles: EntityCounts;
  books: EntityCounts;
}

interface EntityTypeSelectorProps {
  selectedType: SelectedType;
  onTypeChange: (type: SelectedType) => void;
  stats: EntityStats;
  loading?: boolean;
}

const typeTabs: { type: SelectedType; label: string; icon: LucideIcon }[] = [
  { type: 'articles', label: 'Articles', icon: Newspaper },
  { type: 'events', label: 'Événements', icon: Calendar },
  { type: 'albums', label: 'Albums', icon: Image },
  { type: 'books', label: 'Éditions', icon: BookOpen },
];

export const EntityTypeSelector: React.FC<EntityTypeSelectorProps> = ({
  selectedType,
  onTypeChange,
  stats,
  loading = false,
}) => {
  const getTypeStats = (type: SelectedType): EntityCounts => {
    return stats[type] || { drafted: 0, published: 0 };
  };

  return (
    <div className='flex flex-wrap gap-3 mb-6'>
      {typeTabs.map((tab) => {
        const counts = getTypeStats(tab.type);
        const isActive = selectedType === tab.type;

        return (
          <button
            key={tab.type}
            onClick={() => onTypeChange(tab.type)}
            disabled={loading}
            className={`px-4 py-3 rounded-lg font-medium transition-all whitespace-nowrap ${
              isActive
                ? 'bg-blue-600 text-white shadow-md'
                : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-500 hover:text-blue-600'
            } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            <div className='flex items-center gap-2'>
              <tab.icon className='w-5 h-5' />
              <span className='font-semibold'>{tab.label}</span>
            </div>
            <div className='flex items-center gap-2 mt-1 text-xs'>
              <span
                className={`px-2 py-0.5 rounded ${
                  isActive
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                Brouillons: {counts.drafted}
              </span>
              <span
                className={`px-2 py-0.5 rounded ${
                  isActive
                    ? 'bg-green-500 text-white'
                    : 'bg-gray-100 text-gray-600'
                }`}
              >
                Publiés: {counts.published}
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
};
